<template>
  <div>
    <v-card
      :class="$style.modelrepair_widget"
      :loading="repair_models_loading"
    >
      <div :class="$style.widget_title">
        <span class="display-3">Распределение моделей в ремонте</span>
      </div>
      <div class="mb-6">
        <span
          v-if="!is_filters"
          class="subheading"
        >
          за текущий месяц
        </span>
      </div>

      <template v-if="is_filters">
        <div class="d-flex mb-6">
          <v-row>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <DatepickerRange
                :date_prop="filters.repair_model_dates"
                label="Период создания"
                type_date='period'
                :class="[$style.datepicker, $style.filterField, $style.datepickerMargin]"
                dense
                clearable
                range
                small_info
                @date_changed="handle_change_dates"
              />
            </v-col>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <Select
                :value="filters.repair_model_organization_id"
                :items="orgs"
                :class="[$style.select, $style.filterField]"
                label="Организация"
                item-text="name_organization"
                item_value="id"
                multiple
                dense
                hide-details
                clearable
                blue_icon
                @input="filter_update('repair_model_organization_id', $event, 'repair_models')"
              />
            </v-col>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <Select
                :value="filters.repair_model_client_id"
                :items="clients"
                :class="[$style.select, $style.filterField]"
                label="Клиент"
                item-text="name_client_company"
                item_value="client_id"
                dense
                hide-details
                clearable
                blue_icon
                @input="filter_update('repair_model_client_id', $event, 'repair_models')"
              />
            </v-col>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <Select
                :value="filters.repair_model_operation_type_id"
                :items="operation_types"
                :class="[$style.select, $style.filterField]"
                label="Вид операции"
                item-text="name"
                item_value="id"
                multiple
                dense
                hide-details
                clearable
                blue_icon
                @input="filter_update('repair_model_operation_type_id', $event, 'repair_models')"
              />
            </v-col>
          </v-row>
        </div>
      </template>

      <template v-if="is_filters">
        <div
          id="chart"
          v-if="analytics_repair_models_count > 0 && analytics_repair_models.length > 0"
        >
          <apexchart
            type="bar"
            :height="chart_height"
            :width="chartWidth"
            :options="AnalyticsChartOptions"
            :series="analytics_series"
          />
        </div>
        <div :style="{ marginTop: analytics_repair_models_count > 0 ? '0' : '36px' }">
          <span class="subheading">Всего моделей в ремонте:</span>
          <span class="subheading font-weight-bold ml-2">{{ analytics_repair_models_count }}</span>
        </div>
      </template>
      <template v-else>
        <div
          id="chart"
          v-if="repair_models_count > 0 && repair_models.length > 0"
        >
          <apexchart
            type="bar"
            height="350"
            :width="chartWidth"
            :options="chartOptions"
            :series="series"
          />
        </div>
        <div :style="{ marginTop: repair_models_count > 0 ? '0' : '36px' }">
          <span class="subheading">Всего моделей в ремонте:</span>
          <span class="subheading font-weight-bold ml-2">{{ repair_models_count }}</span>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script lang="ts">
import {
  Vue, Component, Watch, Prop, Emit,
} from 'vue-property-decorator';
import { widgets_store, global_store, analytics_store } from '@/store';
import { Select, DatepickerRange } from '@/components/global';
import { AnalyticsFilters } from '@/modules/analytics/types';

const { state } = widgets_store;

const analytics_state = analytics_store.state;

@Component({
  components: {
    apexchart: () => import('vue-apexcharts'),
    Select,
    DatepickerRange,
  },
})
export default class WidgetModelsRepair extends Vue {
  @Prop({ type: Boolean, default: false })
  is_filters!: boolean;

  @Emit()
  filter_update(key: keyof AnalyticsFilters, value: unknown, section: string) {
    analytics_store.mutations.UPD_FILTER_KEY({ key, value, section });
  }

  mounted() {
    this.calculateYAxisMaxWidth();
  }

  get chart_height() {
    const length = this.analytics_repair_models ? this.analytics_repair_models.length : 0;

    return (length * 35).toString();
  }

  get operation_types() {
    return analytics_state.operation_types;
  }

  get orgs() {
    return analytics_state.organizations;
  }

  get analytics_repair_models() {
    const response_filters = this.has_response_filters();

    return response_filters
      ? analytics_state.response_filters.repair_models.items
      : analytics_state.analytics.repair_models.items;
  }

  get analytics_repair_models_count() {
    const response_filters = this.has_response_filters();

    return response_filters
      ? analytics_state.response_filters.repair_models.total_count
      : analytics_state.analytics.repair_models.total_count;
  }

  get is_tablet() {
    return global_store.state.isTablet;
  }

  get is_mobile() {
    return global_store.state.isMobile;
  }

  get modify_repair_models() {
    return this.splitAndCreateArray(state.widgets.repair_models?.items.map((item) => item.title));
  }

  get analytics_modify_repair_models() {
    const response_filters = this.has_response_filters();

    if (response_filters) {
      return this.splitAndCreateArray(analytics_state.response_filters.repair_models.items?.map((item) => item.title));
    }
    return this.splitAndCreateArray(analytics_state.analytics.repair_models.items?.map((item) => item.title));
  }

  get clients() {
    return analytics_state.clients;
  }

  get filters() {
    return analytics_state.filters;
  }

  get repair_models_loading() {
    return analytics_state.repair_models_loading;
  }

  LabelMaxLength = 70;

  splitAndCreateArray(array: any) {
    const maxLength = this.LabelMaxLength;
    const result = [];

    // eslint-disable-next-line no-restricted-syntax
    for (let str of array) {
      const substrings = [];

      while (str.length > maxLength) {
        let sub = str.substring(0, maxLength);
        const lastSpaceIndex = sub.lastIndexOf(' ');

        if (lastSpaceIndex !== -1) {
          sub = sub.substring(0, lastSpaceIndex);
        }

        substrings.push(sub);
        str = str.substring(sub.length + 1);
      }

      substrings.push(str);
      result.push(substrings);
    }
    return result;
  }

  get repair_models() {
    return state.widgets.repair_models?.items;
  }

  get repair_models_count() {
    return state.widgets.repair_models?.total_count;
  }

    chartWidth = '100%';

    // НАСТРОЙКИ ГРАФИКА
    get chartOptions() {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;

      return {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false,
          },
        },
        tooltip: {
          enabled: true,
          y: {
            formatter(y: number) {
              if (typeof y !== 'undefined') {
                return y.toFixed(0);
              }
              return y;
            },
          },
        },

        plotOptions: {
          bar: {
            borderRadius: 1,
            horizontal: true,
            barHeight: '86%',
            distributed: true,
            dataLabels: {
              position: 'top',
            },
          },

        },
        dataLabels: {
          enabled: true,
          formatter(val: any, opt: any) {
            const index = opt.dataPointIndex;

            if (self.repair_models && self.repair_models[index]) {
              const { percent } = self.repair_models[index];

              return `${val} (${percent}%)`;
            }
            return val;
          },
          style: {
            fontSize: '12px',
            fontWeight: '700',
            colors: ['#13141C'],
          },
          offsetX: 20,
          offsetY: 0,
          textAnchor: 'start',
          strokeWidth: 0,
        },
        legend: {
          show: this.is_tablet || this.is_mobile,
          horizontalAlign: 'left',
          offsetX: -8,
          // width: 600,
        },
        xaxis: {
          categories: this.modify_repair_models,
          max: this.max_count,
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        grid: {
          show: false,
        },
        yaxis: {
          labels: {
            show: !this.is_tablet && !this.is_mobile,
            maxWidth: 1000,
            style: {
              fontSize: '12px',
              fontWeight: '400',
              colors: ['#13141C'],
            },

          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },

        colors: [
          '#DCE1F3', '#DCE0EB', '#F5DCE0', '#C1CDF0', '#C9CDDD', '#F5C3CA', '#9BB2EC', '#B2B7CA',
          '#F2A0AB', '#7498E8', '#7498E8', '#F07987', '#477DE4', '#82869F',
        ],
      };
    }

    get AnalyticsChartOptions() {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;

      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: false,
          },
        },
        tooltip: {
          enabled: true,
          y: {
            formatter(y: number) {
              if (typeof y !== 'undefined') {
                return y.toFixed(0);
              }
              return y;
            },
          },
        },

        plotOptions: {
          bar: {
            borderRadius: 1,
            horizontal: true,
            barHeight: '86%',
            distributed: true,
            dataLabels: {
              position: 'top',
            },
          },

        },
        dataLabels: {
          enabled: true,
          formatter(val: any, opt: any) {
            const index = opt.dataPointIndex;

            if (self.analytics_repair_models !== undefined) {
              const { percent } = self.analytics_repair_models[index];

              return `${val} (${percent}%)`;
            }
            return '';
          },
          style: {
            fontSize: '12px',
            fontWeight: '700',
            colors: ['#13141C'],
          },
          offsetX: 70,
          offsetY: 0,
          textAnchor: 'end',
          strokeWidth: 0,
        },
        legend: {
          show: this.is_tablet || this.is_mobile,
          horizontalAlign: 'left',
          offsetX: -8,
          // width: 600,
        },
        xaxis: {
          categories: this.analytics_modify_repair_models,
          max: this.analytics_max_count,
          labels: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        grid: {
          show: false,
        },
        yaxis: {
          labels: {
            show: !this.is_tablet && !this.is_mobile,
            maxWidth: 1000,
            style: {
              fontSize: '12px',
              fontWeight: '400',
              colors: ['#13141C'],
            },

          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },

        colors: [
          '#DCE1F3', '#DCE0EB', '#F5DCE0', '#C1CDF0', '#C9CDDD', '#F5C3CA', '#9BB2EC', '#B2B7CA',
          '#F2A0AB', '#7498E8', '#7498E8', '#F07987', '#477DE4', '#82869F',
        ],
      };
    }

    get series() {
      return [
        {
          name: 'Количество',
          data: this.repair_models?.map((item) => item.value),
        },
      ];
    }

    get analytics_series() {
      return [
        {
          name: 'Количество',
          data: this.analytics_repair_models?.map((item) => item.value),
        },
      ];
    }

    get max_count() {
      let max = 0;

      if (this.repair_models) {
        this.repair_models.forEach((item) => {
          if (item.value > max) {
            max = item.value;
          }
        });
      }
      return max + 4;
    }

    get analytics_max_count() {
      let max = 0;

      if (this.analytics_repair_models !== undefined) {
        this.analytics_repair_models.forEach((item) => {
          if (item.value > max) {
            max = item.value;
          }
        });
      }
      return max + 4;
    }

    // Расчитываем ширину экрана и отдаем ширину для построения графика при создании компонента
    calculateYAxisMaxWidth() {
      if (window.innerWidth > 1440) {
        this.LabelMaxLength = 90;
      } if (window.innerWidth <= 1440) {
        this.LabelMaxLength = 48;
      }
    }

  // Следим за изменением ширины виджета
  @Watch('$vuetify.breakpoint.width')
    onWidthChange(newWidth: number) {
      if (newWidth >= 1440) {
        this.LabelMaxLength = 90;
      } else if (newWidth < 1440) {
        this.LabelMaxLength = 48;
      }
    }

  handle_change_dates(dates: string[]) {
    const dates_unformatted = [...dates];
    const dates_formatted = dates_unformatted.map((date) => new Date(date));

    if (dates_formatted[1] < dates_formatted[0]) {
      [
        dates_unformatted[0], dates_unformatted[1],
      ] = [
        dates[1], dates[0],
      ];
    }
    this.filter_update('repair_model_dates', dates_unformatted, 'repair_models');
  }

  has_response_filters() {
    return (
      analytics_state.filters.repair_model_client_id !== null
      || analytics_state.filters.repair_model_dates.length > 0
      || analytics_state.filters.repair_model_operation_type_id.length > 0
      || analytics_state.filters.repair_model_organization_id.length > 0
    );
  }
}
</script>

<style module lang="scss">
@import '@/scss/common/filter-blocks.scss';

.modelrepair_widget {
  border: solid 1px black;
  // height: 588px;
  display: flex;
  flex-direction: column;
  padding: 24px;
}
.widget_title {
  font-style: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #13141C;
}
.filterField {
  max-width: 100% !important;
}
.datepickerMargin {
  margin-bottom: -14px;
}
</style>
